import {
  CreateMonitoringsDto,
  MonitoringsEntity,
  UpdateMonitoringsDto
} from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, MonitoringStatus, UserRole } from '@crm/interface';
import { API_RESPONSE, prepareUpdateFields } from '@crm/utils';
import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { In, Repository } from 'typeorm';

@Injectable()
export class MonitoringsService {
  private readonly logger = new Logger(MonitoringsService.name);

  constructor(
    @InjectRepository(MonitoringsEntity)
    private readonly monitoringsRepo: Repository<MonitoringsEntity>,
  ) {}

  // ====== GET ALL ======
  async getAll(): Promise<ApiResponseInterface> {
    try {
      const monitorings = await this.monitoringsRepo.find({
        relations: ['proposal', 'company', 'created_by', 'updated_by', 'attachments'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.MONITORINGS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.MONITORINGS.GET_ALL.MESSAGE,
        data: monitorings,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.MONITORINGS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.MONITORINGS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // ====== GET FOR SUPERVISOR (created by managers and supervisors) ======
  async getForSupervisor(): Promise<ApiResponseInterface> {
    try {
      const monitorings = await this.monitoringsRepo.find({
        where: { created_by: { role: In([UserRole.MANAGER, UserRole.SUPERVISOR]) } },
        relations: ['proposal', 'company', 'created_by', 'updated_by', 'attachments'],
      });
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.MONITORINGS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.MONITORINGS.GET_ALL.MESSAGE,
        data: monitorings,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.MONITORINGS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.MONITORINGS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // ====== UPDATE STATUS ONLY (management + supervisor) ======
  async updateStatus(id: string, status: MonitoringStatus, updatedById?: string): Promise<ApiResponseInterface> {
    try {
      const monitoring = await this.monitoringsRepo.findOne({ where: { id } });
      if (!monitoring) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.MONITORINGS.UPDATE.CODE,
          message: API_RESPONSE.ERROR.MONITORINGS.UPDATE.MESSAGE,
          data: null,
        };
      }
      monitoring.status = status;
      if (updatedById) monitoring.updated_by = { id: updatedById } as any;
      await this.monitoringsRepo.save(monitoring);
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.MONITORINGS.UPDATE.CODE,
        message: 'Monitoring status updated successfully.',
        data: monitoring,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.MONITORINGS.UPDATE.CODE,
        message: API_RESPONSE.ERROR.MONITORINGS.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== GET BY USER ID ======
  async getByUserId(userId: string): Promise<ApiResponseInterface> {
    try {
      const monitorings = await this.monitoringsRepo.find({
        where: { created_by: { id: userId } },
        relations: ['proposal', 'company', 'created_by', 'updated_by', 'attachments'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.MONITORINGS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.MONITORINGS.GET_BY_ID.MESSAGE,
        data: monitorings,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.MONITORINGS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.MONITORINGS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // ====== GET BY ID ======
  async getById(id: string): Promise<ApiResponseInterface> {
    try {
      const monitoring = await this.monitoringsRepo.findOne({
        where: { id },
        relations: ['proposal', 'company', 'created_by', 'updated_by', 'attachments'],
      });

      if (!monitoring) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.MONITORINGS.GET_BY_ID.CODE,
          message: API_RESPONSE.ERROR.MONITORINGS.GET_BY_ID.MESSAGE,
          data: null,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.MONITORINGS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.MONITORINGS.GET_BY_ID.MESSAGE,
        data: monitoring,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.MONITORINGS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.MONITORINGS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // ====== CREATE ======
  async create(dto: CreateMonitoringsDto): Promise<ApiResponseInterface> {
    try {
      // Map relation IDs to objects
      const createData: any = { ...dto };
      createData.proposal = { id: dto.proposal_id };
      createData.company = { id: dto.company_id };

      if (dto.created_by_id) createData.created_by = { id: dto.created_by_id };
      if (dto.updated_by_id) createData.updated_by = { id: dto.updated_by_id };

      // Remove raw ID fields
      delete createData.proposal_id;
      delete createData.company_id;
      delete createData.created_by_id;
      delete createData.updated_by_id;

      const newMonitoring = this.monitoringsRepo.create(createData);
      await this.monitoringsRepo.save(newMonitoring);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.MONITORINGS.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.MONITORINGS.CREATE.MESSAGE,
        data: newMonitoring,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.MONITORINGS.CREATE.CODE,
        message: API_RESPONSE.ERROR.MONITORINGS.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== UPDATE ======
  async update(dto: UpdateMonitoringsDto): Promise<ApiResponseInterface> {
    try {
      const monitoring = await this.monitoringsRepo.findOne({ where: { id: dto.id } });
      if (!monitoring) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.MONITORINGS.UPDATE.CODE,
          message: API_RESPONSE.ERROR.MONITORINGS.UPDATE.MESSAGE,
          data: null,
        };
      }

      const updateData: any = prepareUpdateFields(dto, { updated_by_id: 'updated_by' });

      // Map relation IDs if present
      if (dto.company_id) updateData.company = { id: dto.company_id };

      Object.assign(monitoring, updateData);
      await this.monitoringsRepo.save(monitoring);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.MONITORINGS.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.MONITORINGS.UPDATE.MESSAGE,
        data: monitoring,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.MONITORINGS.UPDATE.CODE,
        message: API_RESPONSE.ERROR.MONITORINGS.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== SOFT DELETE ======
  async softDelete(dto: UpdateMonitoringsDto): Promise<ApiResponseInterface> {
    try {
      const monitoring = await this.monitoringsRepo.findOne({ where: { id: dto.id } });
      if (!monitoring) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.MONITORINGS.DELETE.CODE,
          message: API_RESPONSE.ERROR.MONITORINGS.DELETE.MESSAGE,
          data: null,
        };
      }

      // For soft delete, we can change status to a "deleted" enum if exists, otherwise nullify relations
      monitoring.status = MonitoringStatus.DELETED;

      await this.monitoringsRepo.save(monitoring);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.MONITORINGS.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.MONITORINGS.DELETE.MESSAGE,
        data: monitoring,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.MONITORINGS.DELETE.CODE,
        message: API_RESPONSE.ERROR.MONITORINGS.DELETE.MESSAGE,
        data: error,
      };
    }
  }
}